// secretRedaction.js — mask secret-shaped strings in run_command output
// (E1-T02, sprint agent-arch-hardening).
//
// THE EXFIL PATH: the Code agent can run `env`, `cat .env`, `printenv` — and the
// stdout flows BACK into the conversation → is re-sent to the cloud LLM → is
// persisted to D1. So an API key in the environment leaks off the user's Mac the
// moment the model reads it. This module masks high-confidence secret shapes in
// that OUTPUT before it ever enters the conversation. Applied ONLY to run_command
// results (file reads of source the user already has are fine; the env is what's
// dangerous), and ONLY to output — the env passed to the shell is untouched so
// `gh`/`git` keep their tokens.
//
// DESIGN — PRECISION. We mask only shapes that are essentially always secrets
// (provider key prefixes, AWS AKIA, JWT triplets) plus the VALUE side of an
// env-dump line for known-sensitive var names. Normal prose / code / file
// listings come back BYTE-FOR-BYTE. Pure + synchronous; non-string → ''.

const MASK = '***REDACTED***';

// Each entry masks a specific secret SHAPE. Order matters only for overlap; these
// shapes are mutually exclusive enough that order is cosmetic. Functions return
// the masked replacement so we can preserve a recognizable prefix where useful.
const PATTERNS = [
  // OpenRouter sk-or-… (more specific than bare sk-, listed first). Up to a
  // version/hash tail of word chars + hyphens.
  {
    re: /sk-or-[A-Za-z0-9_-]{8,}/g,
    replace: () => `sk-or-${MASK}`,
  },
  // OpenAI / generic sk- key, including modern PREFIXED formats
  // (sk-proj-…, sk-ant-…, sk-live-…, sk-test-…). The old /sk-[A-Za-z0-9]{20,}/
  // broke at the hyphen in `proj-`, so `sk-proj-<40 chars>` leaked (smoke
  // 2026-06-08). Now: \bsk- + a first key char + a 19+ body that TOLERATES
  // internal hyphens/underscores. Anchored on \bsk- and requiring the long body
  // so short tokens (sk-test) and hyphenated prose (ask-me-about-…) don't match.
  {
    re: /\bsk-[A-Za-z0-9][A-Za-z0-9_-]{19,}/g,
    replace: () => `sk-${MASK}`,
  },
  // GitHub fine-grained PAT: github_pat_ + base62/underscore body.
  {
    re: /github_pat_[A-Za-z0-9_]{20,}/g,
    replace: () => `github_pat_${MASK}`,
  },
  // GitHub classic tokens: ghp_/gho_/ghs_/ghu_/ghr_ + 30+ chars.
  {
    re: /gh[posur]_[A-Za-z0-9]{30,}/g,
    replace: (m) => `${m.slice(0, 4)}${MASK}`,
  },
  // AWS access key id.
  {
    re: /AKIA[0-9A-Z]{16}/g,
    replace: () => `AKIA${MASK}`,
  },
  // JWT: three base64url segments. The header almost always starts eyJ.
  {
    re: /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g,
    replace: () => MASK,
  },
  // PEM private key block (RSA/EC/OPENSSH/generic) — mask the whole armored block.
  {
    re: /-----BEGIN (?:[A-Z0-9]+ )*PRIVATE KEY-----[\s\S]*?-----END (?:[A-Z0-9]+ )*PRIVATE KEY-----/g,
    replace: () => `-----BEGIN PRIVATE KEY-----${MASK}-----END PRIVATE KEY-----`,
  },
  // Google API key — AIza + 35 chars (very specific prefix, ~zero false positives).
  {
    re: /AIza[0-9A-Za-z_-]{35}/g,
    replace: () => `AIza${MASK}`,
  },
  // Google OAuth access token ya29.…
  {
    re: /ya29\.[0-9A-Za-z_-]{20,}/g,
    replace: () => `ya29.${MASK}`,
  },
  // Slack token: xoxb-/xoxp-/xoxa-/xoxr-/xoxs- + body.
  {
    re: /xox[baprs]-[A-Za-z0-9-]{10,}/g,
    replace: (m) => `${m.slice(0, 5)}${MASK}`,
  },
  // Bearer token in a header/log line (keep "Bearer ", mask the value). 20+ body so
  // prose like "Bearer of bad news" never matches.
  {
    re: /\bBearer\s+[A-Za-z0-9._-]{20,}/g,
    replace: () => `Bearer ${MASK}`,
  },
  // AWS INI credentials (lowercase keys — ENV_SECRET_LINE_RE is uppercase-only):
  // `aws_secret_access_key = …`, `aws_session_token = …`.
  {
    re: /\b(aws_secret_access_key|aws_session_token)\s*=\s*\S+/gi,
    replace: (m) => m.replace(/=\s*\S+/, `=${MASK}`),
  },
  // npmrc / .netrc style auth tokens: `_authToken=…`, `_auth=…`, `_password=…`.
  {
    re: /\b(_authToken|_auth|_password)=\S+/gi,
    replace: (m) => `${m.split('=')[0]}=${MASK}`,
  },
  // Inline URL credentials (.git-credentials, proxy URLs): https://user:token@host.
  {
    re: /\b(https?:\/\/[^:@\s/]+):[^@\s/]+@/gi,
    replace: (_m, userPart) => `${userPart}:${MASK}@`,
  },
];

// Env-dump line whose VAR NAME is sensitive: mask the value, keep `NAME=`. Only
// fires on a NON-EMPTY value (an empty `FOO_PASSWORD=` stays intact — no noise).
// Anchored to a line so it never eats a sentence containing "password".
const ENV_SECRET_LINE_RE =
  /^([A-Za-z][A-Za-z0-9_]*(?:API_KEY|_KEY|TOKEN|SECRET|PASSWORD|PASSWD|PRIVATE_KEY|ACCESS_KEY)[A-Za-z0-9_]*)=([^\n]+)$/gim;

/**
 * Redact high-confidence secret-shaped strings from text (e.g. run_command output).
 * Returns the input byte-for-byte when nothing matches. Non-string → ''. Pure.
 *
 * @param {string} text
 * @returns {string}
 */
export function redactSecrets(text) {
  if (typeof text !== 'string' || text.length === 0) return '';
  let out = text;
  for (const { re, replace } of PATTERNS) {
    out = out.replace(re, replace);
  }
  // Env-dump var=value lines for sensitive var names: mask the value, keep name.
  // Skip if the value was already fully masked above (avoid double-masking shape).
  out = out.replace(ENV_SECRET_LINE_RE, (full, name, value) => {
    if (value.trim() === '' || value.trim() === MASK || value.includes(MASK)) return full;
    return `${name}=${MASK}`;
  });
  return out;
}

// THE read_file EXFIL GAP (sibling of the run_command path above). The default
// toolPolicy denyPaths covers HOME secret dirs (.ssh/.aws/...) but deliberately
// omits `.env` — a substring deny would false-positive `.environment`. So a direct
// `read_file({ path: '.env' })` streams the repo's real keys into the conversation →
// cloud LLM → D1. `cat .env` via run_command is already redacted; this closes the
// direct-read path. We REFUSE the read (vs redacting content) because a redacted read
// that round-trips through write_file would overwrite the user's real keys with the
// mask. PRECISE: basename-anchored, so `.env.example`/`environment.ts` stay readable.
const SECRET_FILE_ALLOW_RE = /^\.env\.(example|sample|template|dist|defaults?)$/i;
const SECRET_FILE_BASENAMES = new Set([
  '.env', '.netrc', '.git-credentials', 'id_rsa', 'id_ed25519', 'id_ecdsa', 'id_dsa',
]);

/**
 * True when reading this path would expose a high-confidence secret FILE (the
 * .env family, private keys, credential stores). Placeholder env files
 * (.env.example/.sample/.template/.dist) are safe and return false.
 * Basename-anchored — never fires on `environment.ts` or a `.env`-substring path.
 *
 * @param {string} relPath path relative to the session root
 * @returns {boolean}
 */
export function isSecretFilePath(relPath) {
  const base = String(relPath ?? '').split(/[\\/]/).pop().toLowerCase();
  if (!base) return false;
  if (SECRET_FILE_ALLOW_RE.test(base)) return false;
  if (SECRET_FILE_BASENAMES.has(base)) return true;
  if (/^\.env\./.test(base)) return true; // .env.local, .env.production, ...
  return base.endsWith('.pem');
}

export const _internals = { PATTERNS, ENV_SECRET_LINE_RE, MASK, SECRET_FILE_BASENAMES };
